import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2, Loader2, Save } from 'lucide-react';
import { toast, Button } from '@strivelabs/ui-core';
import { RichTextEditor } from '@strivelabs/ui-blocks';
import type { JSONContent } from '@strivelabs/ui-blocks';
import { z } from 'zod/v4';
import type { BehaviourAgentExperienceAction } from '@/types/ui-blocks';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { useUpdateBehaviourActionState } from '@/hooks/useUpdateBehaviourActionState';
import { FullscreenSlotOverlay, SlotContainer } from './shared';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';

// Schema for validating saved action state
const ContentPreviewStateSchema = z.object({
  content: z.custom<JSONContent>((val) => {
    return typeof val === 'object' && val !== null;
  }),
});

// Toast messages
const CONTENT_PREVIEW_MESSAGES = {
  SAVE_SUCCESS: 'Content saved successfully',
  SAVE_ERROR: 'Failed to save content',
  DISCARD_SUCCESS: 'Changes discarded',
} as const;

export interface ContentPreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
}

export function ContentPreviewSlot({
  slotId,
  action,
  chatHistoryId,
  behaviourActionState,
}: ContentPreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
  });
  const { workflowInstanceId, workflowExecutionId } = useChatMetadata();
  const [isExpanded, setIsExpanded] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);

  // Resolve saved state (Tiptap JSON stored under action.id key) with type safety
  const actionState = action.id && behaviourActionState
    ? ContentPreviewStateSchema.safeParse(behaviourActionState[action.id])
    : null;
  const savedJSON = actionState?.success ? actionState.data.content : null;
  const originalMarkdown = action.data?.content || '';

  // Track the current working content and whether there are unsaved changes
  const [currentJSON, setCurrentJSON] = useState<JSONContent | null>(savedJSON ?? null);
  const [hasChanges, setHasChanges] = useState(false);

  // Sync currentJSON when savedJSON updates from backend (after save or chat history refetch)
  useEffect(() => {
    if (savedJSON && !hasChanges) {
      setCurrentJSON(savedJSON);
    }
  }, [savedJSON, hasChanges]);

  const saveMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      // Mark changes as saved - the next backend fetch will update savedJSON
      setHasChanges(false);
      toast({ title: CONTENT_PREVIEW_MESSAGES.SAVE_SUCCESS });
    },
    onError: (error) => {
      toast({
        title: CONTENT_PREVIEW_MESSAGES.SAVE_ERROR,
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    if (!currentJSON || !chatHistoryId || !action.id || !workflowInstanceId || !workflowExecutionId) {
      return;
    }
    saveMutation.mutate({
      workflowInstanceId,
      workflowExecutionId,
      chatHistoryId,
      behaviourActionStateId: action.id,
      state: { content: currentJSON },
    });
  };

  const handleDiscard = () => {
    if (savedJSON) {
      setCurrentJSON(savedJSON);
    }
    setHasChanges(false);
    toast({ title: CONTENT_PREVIEW_MESSAGES.DISCARD_SUCCESS });
  };

  const handleJSONChange = (json: JSONContent) => {
    setCurrentJSON(json);
    setHasChanges(true);
  };

  // Resolve the chat container on mount
  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  const renderEditor = () => (
    <div className="space-y-4">
      <RichTextEditor
        content={originalMarkdown}
        contentJSON={currentJSON ?? undefined}
        editable
        onChangeJSON={handleJSONChange}
        showToolbar
      />
    </div>
  );

  const renderCanvasHeaderActions = () => (
    <div className="flex items-center gap-1">
      <Button
        onClick={handleSave}
        disabled={!hasChanges || saveMutation.isPending || !chatHistoryId}
        size="sm"
        variant="outline"
        className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-sm font-normal tracking-[-0.28px]"
      >
        {saveMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        {saveMutation.isPending ? 'Saving...' : 'Save'}
      </Button>
      {hasChanges && savedJSON && (
        <Button
          onClick={handleDiscard}
          disabled={saveMutation.isPending}
          size="sm"
          variant="ghost"
          className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-sm font-normal tracking-[-0.28px]"
        >
          Discard
        </Button>
      )}
    </div>
  );

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    return (
      <>
        <SlotCanvasPreview
          id={resolvedSlotId}
          experience={action.experience}
          title={resolvedTitle}
          isActive={canvasRegistration.isOpen}
          onClick={canvasRegistration.open}
        />
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
              headerActions={renderCanvasHeaderActions()}
            >
              <div className="p-10 space-y-7">{renderEditor()}</div>
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------
  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10 size-6 flex items-center justify-center rounded-md bg-white/60 border border-black/10 shadow-sm hover:bg-white/80 transition-colors"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </button>

        {/* Slot content */}
        <div className="p-10 space-y-7">
          {renderEditor()}
        </div>

        {/* Footer with Save and Discard buttons */}
        <div className="relative border-t border-black/[0.04] h-12 flex items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending || !chatHistoryId}
              size="sm"
              variant="outline"
              className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-sm font-normal tracking-[-0.28px]"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
            {hasChanges && savedJSON && (
              <Button
                onClick={handleDiscard}
                disabled={saveMutation.isPending}
                size="sm"
                variant="ghost"
                className="flex items-center gap-2 px-2.5 py-1 rounded-lg text-sm font-normal tracking-[-0.28px]"
              >
                Discard
              </Button>
            )}
          </div>
        </div>
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay
            onClose={() => setIsExpanded(false)}
            portalTarget={chatRoot}
          >
            {renderEditor()}
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>
    </>
  );
}
